"use client";

import { c as _c } from "react/compiler-runtime";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { numberWithCommas } from "@/helpers/Helpers";
import Pagination from "../pagination/Pagination";
import { SortDirection } from "@/entities/ISort";
import { LeaderboardCollector } from "./LeaderboardCollector";
import type { NftTDHRanked } from "./NFTLeaderboard";
import { fetchNftTdhResults, PAGE_SIZE } from "./NFTLeaderboard";
import { faSquareCaretUp, faSquareCaretDown } from "@fortawesome/free-solid-svg-icons";
const tableHeaderClassName = "tw-whitespace-nowrap tw-border-0 tw-border-b tw-border-solid tw-border-iron-800 tw-px-2 tw-py-2 tw-text-center tw-text-xs tw-font-semibold tw-leading-4 tw-text-iron-400 md:tw-px-4 md:tw-py-3";
const tableCellClassName = "tw-whitespace-nowrap tw-border-0 tw-border-b tw-border-solid tw-border-iron-800 tw-px-2 tw-py-2 tw-text-center tw-text-xs tw-font-medium tw-leading-5 tw-text-iron-100 md:tw-px-4 md:tw-py-3 md:tw-text-sm";
const MEME_LAB_BALANCE_SORT = "balance";
interface Props {
  contract: string;
  nftId: number;
}
type LeaderboardRequest = {
  readonly page: number;
  readonly sortDirection: SortDirection;
};
type MemeLabLeaderboardData = {
  readonly count: number;
  readonly data: NftTDHRanked[];
};
export default function MemeLabLeaderboard(props) {
  const $ = _c(40);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      page: 1,
      sortDirection: SortDirection.DESC
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [request, setRequest] = useState(t0);
  let t1;
  if ($[1] !== props.contract || $[2] !== props.nftId || $[3] !== request.page || $[4] !== request.sortDirection) {
    t1 = {
      queryKey: ["meme-lab-leaderboard", props.contract, props.nftId, request.page, MEME_LAB_BALANCE_SORT, request.sortDirection],
      queryFn: async () => {
        const response = await fetchNftTdhResults(props.contract, props.nftId, "", request.page, MEME_LAB_BALANCE_SORT, request.sortDirection);
        const data = response.data.map((lead, index) => {
          const rank = index + 1 + (request.page - 1) * PAGE_SIZE;
          return {
            ...lead,
            rank
          };
        });
        return {
          count: response.count,
          data
        };
      },
      placeholderData: keepPreviousData
    };
    $[1] = props.contract;
    $[2] = props.nftId;
    $[3] = request.page;
    $[4] = request.sortDirection;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const {
    data: leaderboardData,
    isFetched
  } = useQuery(t1);
  const totalResults = leaderboardData?.count ?? 0;
  let t2;
  if ($[6] !== leaderboardData?.data) {
    t2 = leaderboardData?.data ?? [];
    $[6] = leaderboardData?.data;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const leaderboard = t2;
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = function handleSortChange(sortDirection) {
      setRequest({
        page: 1,
        sortDirection
      });
    };
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const handleSortChange = t3;
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = function handlePageChange(newPage) {
      setRequest(current => ({
        ...current,
        page: newPage
      }));
    };
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const handlePageChange = t4;
  let t5;
  if ($[10] !== request.sortDirection) {
    const getCaretClassName = function getCaretClassName(sortDirection_0) {
      const active = request.sortDirection === sortDirection_0;
      return `tw-h-3 tw-w-3 tw-transition-colors ${active ? "tw-text-white" : "tw-text-iron-500 hover:tw-text-white"}`;
    };
    t5 = function printSortControls(label) {
      return <span className="tw-flex tw-flex-col tw-items-center"><button type="button" onClick={() => handleSortChange(SortDirection.ASC)} className="tw-m-0 tw-flex tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0" aria-label={`Sort ${label} ascending`}><FontAwesomeIcon icon={faSquareCaretUp} className={getCaretClassName(SortDirection.ASC)} /></button><button type="button" onClick={() => handleSortChange(SortDirection.DESC)} className="tw-m-0 tw-flex tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0" aria-label={`Sort ${label} descending`}><FontAwesomeIcon icon={faSquareCaretDown} className={getCaretClassName(SortDirection.DESC)} /></button></span>;
    };
    $[10] = request.sortDirection;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const printSortControls = t5;
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="tw-mb-4 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3"><h3 className="tw-mb-0 tw-text-lg tw-font-semibold tw-text-iron-100">Collectors leaderboard</h3></div>;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <th scope="col" className="tw-w-12 tw-whitespace-nowrap tw-border-0 tw-border-b tw-border-solid tw-border-iron-800 tw-px-2 tw-py-2 tw-text-center tw-text-xs tw-font-semibold tw-leading-4 tw-text-iron-400 md:tw-w-20 md:tw-px-4 md:tw-py-3">Rank</th>;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== totalResults) {
    t8 = totalResults > 0 && `x${totalResults.toLocaleString()}`;
    $[14] = totalResults;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t8) {
    t9 = <th scope="col" className="tw-min-w-[11rem] tw-whitespace-nowrap tw-border-0 tw-border-b tw-border-solid tw-border-iron-800 tw-px-2 tw-py-2 tw-text-left tw-text-xs tw-font-semibold tw-leading-4 tw-text-iron-400 md:tw-min-w-[18rem] md:tw-px-4 md:tw-py-3">Collector{" "}{t8}</th>;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== printSortControls) {
    t10 = <th scope="col" className={`${tableHeaderClassName} tw-border-l`}><span className="tw-flex tw-items-center tw-justify-center tw-gap-1.5">Balance{printSortControls("Balance")}</span></th>;
    $[18] = printSortControls;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10 || $[21] !== t9) {
    t11 = <thead><tr>{t7}{t9}{t10}</tr></thead>;
    $[20] = t10;
    $[21] = t9;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== leaderboard) {
    t12 = leaderboard.map(_temp);
    $[23] = leaderboard;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t12) {
    t13 = <tbody>{t12}</tbody>;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t11 || $[28] !== t13) {
    t14 = <div className="tw-overflow-x-auto"><table className="tw-w-full tw-min-w-[28rem] tw-border-collapse md:tw-min-w-[640px]">{t11}{t13}</table></div>;
    $[27] = t11;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== request.page || $[31] !== totalResults) {
    t15 = totalResults > 0 && <div className="tw-flex tw-justify-center tw-pb-3 tw-pt-4"><Pagination page={request.page} pageSize={PAGE_SIZE} totalResults={totalResults} setPage={handlePageChange} /></div>;
    $[30] = request.page;
    $[31] = totalResults;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== isFetched || $[34] !== leaderboard.length) {
    t16 = isFetched && leaderboard.length === 0 && <div className="tw-py-5 tw-text-sm tw-font-medium tw-text-iron-400">No Results found</div>;
    $[33] = isFetched;
    $[34] = leaderboard.length;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t14 || $[37] !== t15 || $[38] !== t16) {
    t17 = <section className="tw-scroll-mt-24 tw-pt-8" id="nft-leaderboard">{t6}{t14}{t15}{t16}</section>;
    $[36] = t14;
    $[37] = t15;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  return t17;
}
function _temp(lead_0) {
  return <tr key={lead_0.consolidation_key} className="odd:tw-bg-transparent even:tw-bg-iron-900/45 hover:tw-bg-iron-900/70"><td className="tw-whitespace-nowrap tw-border-0 tw-border-b tw-border-solid tw-border-iron-800 tw-px-2 tw-py-2 tw-text-center tw-text-xs tw-font-semibold tw-leading-5 tw-text-iron-100 md:tw-px-4 md:tw-py-3 md:tw-text-sm">{numberWithCommas(lead_0.rank)}</td><td className="tw-border-0 tw-border-b tw-border-solid tw-border-iron-800 tw-px-2 tw-py-2 md:tw-px-4 md:tw-py-3"><LeaderboardCollector handle={lead_0.handle} consolidationKey={lead_0.consolidation_key} consolidationDisplay={lead_0.consolidation_display} pfp={lead_0.pfp_url} level={lead_0.level} /></td><td className={`${tableCellClassName} tw-border-l`}>{numberWithCommas(lead_0.balance)}</td></tr>;
}